/**
 * VitnemalsportalenImport - Hent vitnemål fra Vitnemålsportalen (EMREX)
 *
 * Status: Klar for innkobling – IKKE montert i noen rute ennå. Aktiveres
 * når Sikt har bekreftet endepunkter og levert sertifikat-info.
 *
 * Kobles inn i DashboardPage (eller egen "Min lommebok"-side) ved å
 * importere og rendre <VitnemalsportalenImport /> der det skal vises.
 *
 * Komponenten respekterer feature-flag fra backend (/api/candidate/vp/sessions
 * gir 404/501 når deaktivert) og rendres ikke synlig før integrasjonen er aktiv.
 *
 * Tracking: #115, #128, #129, #130
 */

import { useEffect, useState } from 'react';
import { GraduationCap, Loader2, CheckCircle2, AlertCircle, X, ExternalLink, RefreshCw } from 'lucide-react';

interface VitnemalsportalenImportProps {
  /** Hvor mange VP-importerte attester finnes allerede? Styrer kompakt vs. full visning. */
  importedCount?: number;
}

interface InitiateResponse {
  success: boolean;
  sessionId: string;
  // EMREX-flyten krever FORM-POST (ikke GET-redirect) – backend returnerer
  // form-action og felter som vi må konstruere og auto-submitte fra browser.
  formAction: string;
  formMethod: 'POST';
  formFields: { sessionId: string; returnUrl: string };
  empInfo?: { acronym: string; countryCode: string };
  expiresInSeconds: number;
  error?: string;
}

export function VitnemalsportalenImport({ importedCount = 0 }: VitnemalsportalenImportProps = {}) {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sjekk om VP-integrasjonen er aktivert backend-side
  useEffect(() => {
    let cancelled = false;
    fetch('/api/candidate/vp/sessions', { credentials: 'include' })
      .then(res => {
        if (cancelled) return;
        if (res.status === 404 || res.status === 501) {
          setAvailable(false);
          return;
        }
        setAvailable(res.ok);
      })
      .catch(() => { if (!cancelled) setAvailable(false); });
    return () => { cancelled = true; };
  }, []);

  // Vis feilmelding hvis vi kom tilbake fra VP med error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const vpReturn = params.get('vp_return');
    if (vpReturn === 'error') {
      setError(params.get('vp_error') || 'Overføring fra Vitnemålsportalen feilet');
    }
    if (vpReturn) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function handleInitiate() {
    setInitiating(true);
    setError(null);
    try {
      const res = await fetch('/api/candidate/vp/initiate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data: InitiateResponse = await res.json();
      if (!data.success) {
        setError(data.error || 'Kunne ikke starte overføring');
        setInitiating(false);
        return;
      }

      // EMREX-spec krever FORM-POST til EMP-en (ikke GET). Vi bygger et skjult
      // skjema med sessionId + returnUrl og auto-submitter til VP.
      const form = document.createElement('form');
      form.method = data.formMethod;
      form.action = data.formAction;
      form.style.display = 'none';
      Object.entries(data.formFields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Nettverksfeil');
      setInitiating(false);
    }
  }

  function renderConsentModal() {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto">
          <div className="p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h2 className="text-xl font-semibold">Importere fra Vitnemålsportalen</h2>
              <button
                onClick={() => setShowConsent(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Lukk"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4 text-sm text-gray-700">
              <p>
                Du blir nå sendt videre til <strong>Vitnemålsportalen</strong> hos Sikt
                (Tjenesteorgan for Kunnskapsdepartementet). Der logger du inn med
                Feide eller ID-porten og velger hvilke vitnemål du vil dele med kompetanseattest.no.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">Du beholder kontrollen</p>
                    <p className="text-blue-800 mt-1 text-xs">
                      Bare det du selv velger blir overført. Du kan slette importerte
                      vitnemål fra lommeboken når som helst.
                    </p>
                  </div>
                </div>
              </div>
              <h3 className="font-semibold text-gray-900 mt-4">Hva blir hentet</h3>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                <li>Vitnemål og karakterutskrifter du har valgt å dele</li>
                <li>Utstedende institusjon og dato</li>
                <li>Kompetansepoeng (ECTS) og emnekoder</li>
                <li>Ditt fødselsnummer (kreves av Vitnemålsportalen for verifikasjon)</li>
              </ul>
              <h3 className="font-semibold text-gray-900 mt-4">Personvern</h3>
              <p className="text-xs">
                Importerte data lagres i din private lommebok hos kompetanseattest.no, kryptert
                og kun synlig for deg. Vi deler ikke videre uten ditt eksplisitte samtykke.
                Datagrunnlaget er signert av Sikt med digitalt sertifikat og kan verifiseres
                uavhengig.
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowConsent(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium text-sm"
              >
                Avbryt
              </button>
              <button
                onClick={handleInitiate}
                disabled={initiating}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {initiating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Starter...
                  </>
                ) : (
                  <>
                    Gå til Vitnemålsportalen
                    <ExternalLink className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Skjul komponenten helt hvis backend ikke har VP-integrasjon aktivert
  if (available === null || available === false) return null;

  // Kompakt visning kun når det faktisk finnes vitnemål i lommeboken nå.
  // Tidligere fullførte sesjoner alene skal ikke trigge kompakt — kandidat
  // kan ha slettet alle importer og forventer da full info-boks tilbake.
  const isCompact = importedCount > 0;

  // Kompakt visning når kandidaten allerede har importert: liten lenke under
  // attestene istedenfor stor kort-visning
  if (isCompact) {
    return (
      <div className="text-sm text-gray-600 flex items-center justify-center gap-2 py-2 border-t border-gray-200">
        <RefreshCw className="w-4 h-4 text-gray-400" aria-hidden="true" />
        <span>Mangler utdanning fra Vitnemålsportalen?</span>
        <button
          onClick={() => setShowConsent(true)}
          disabled={initiating}
          className="text-blue-600 hover:text-blue-800 font-medium underline disabled:opacity-50"
        >
          Trykk her for å oppdatere innholdet i din lommebok
        </button>
        {error && (
          <div className="ml-2 text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}
        {showConsent && renderConsentModal()}
      </div>
    );
  }

  // Full visning når ingen import er gjort ennå
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="bg-blue-50 text-blue-700 p-2 rounded-lg">
            <GraduationCap className="w-6 h-6" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Hent fra Vitnemålsportalen</h3>
            <p className="text-sm text-gray-600 mt-1">
              Importer dine vitnemål fra høyere utdanning slik at de vises sammen med dine kompetanseattester.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowConsent(true)}
          disabled={initiating}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 whitespace-nowrap"
        >
          Hent vitnemål
        </button>
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {showConsent && renderConsentModal()}
    </div>
  );
}
